import {
  MobileRow,
  MobileRowTitle,
  MobileRowValue,
  MobileTableRowWrap,
  MobileTableWrapper,
  NoResults
} from '@/components/table/mobileTable/mobileTable';

import FormattingTooltip from '@/components/basicComponents/tooltip/dollarsTooltip';
import Tag from '@/components/basicComponents/tag';
import { PublishedInvestmentManagerUpdatesDto, PublishedInvestmentManagerUpdatesSourceEnum } from '@/components/managerUpdates-page/types';
import { getTagStyleType } from '@/components/managerUpdates-page/utils';
import { TextCell } from '@/components/table/styles';
import { TableColumns } from '@/components/table/types';
import { dateFormatter } from '@/shared/utils/date';
import { themeVar } from '@/styles/theme';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { getPublishedUpdatesTableAssetClassValue, getPublishedUpdatesTableCellValue, getPublishedUpdatesTablePacingValue } from './table';

interface PublishedUpdatesMobileTableProps {
  columns: TableColumns[];
  publishedUpdates: PublishedInvestmentManagerUpdatesDto[];
  source: PublishedInvestmentManagerUpdatesSourceEnum;
  openOpportunityDetailsPage: (investmentId: string) => void;
}

const PublishedUpdatesMobileTable = ({ columns, publishedUpdates, source, openOpportunityDetailsPage }: PublishedUpdatesMobileTableProps) => {
  const { t } = useTranslation('enums');

  if (!publishedUpdates.length) {
    return <NoResults />;
  }

  return (
    <MobileTableWrapper>
      {publishedUpdates.map((el, index) => {
        const { investment, legalEntityCommittedCapital } = el || {};
        const assetClassValue = getPublishedUpdatesTableAssetClassValue(investment);
        const pacingValue = getPublishedUpdatesTablePacingValue(investment, t);

        return (
          <CustomMobileTableRowWrap key={investment?.id ?? index}>
            <MobileRow>
              <MobileRowTitle>{columns[0].title}</MobileRowTitle>
              <MobileRowValue>
                <TwoRowsCell>
                  <AssetClass>{`${assetClassValue} (${pacingValue})`}</AssetClass>
                  <StrategyName onClick={() => openOpportunityDetailsPage(investment?.id)}>{investment?.name}</StrategyName>
                  {source === PublishedInvestmentManagerUpdatesSourceEnum.UPDATES_TAB && (
                    <>
                      {legalEntityCommittedCapital?.map((legalEntityCommittedCapitalItem) => {
                        const { entityName, committedCapital } = legalEntityCommittedCapitalItem || {};

                        return (
                          <CustomTextCell key={entityName + committedCapital}>
                            {entityName}: <FormattingTooltip>{committedCapital}</FormattingTooltip>
                          </CustomTextCell>
                        );
                      })}
                    </>
                  )}
                </TwoRowsCell>
              </MobileRowValue>
            </MobileRow>

            {el.publishedUpdates.map((update, index) => (
              <MobileRow key={`${el?.investment?.id ?? index}-${update?.documentField?.id}`}>
                <MobileRowTitle>{update?.documentField?.name}</MobileRowTitle>
                <MobileRowValue>
                  {getPublishedUpdatesTableCellValue(
                    update?.documentField?.type,
                    update?.documentFieldAnswer?.value,
                    update?.documentFieldAnswer?.commentary
                  )}
                </MobileRowValue>
              </MobileRow>
            ))}

            <MobileRow>
              <MobileRowTitle>{columns[columns.length - 1].title}</MobileRowTitle>
              <MobileRowValue>
                <TwoRowsCell>
                  <TextCell>{!!el?.lastUpdate ? dateFormatter(format(new Date(el?.lastUpdate), 'yyyy-MM-dd'), 'yyyy-MM-dd') : '--'}</TextCell>
                  {el?.sentimentFlag && el?.sentimentFlagLabel && (
                    <Tag title={el?.sentimentFlagLabel} size="md" styleType={getTagStyleType(el?.sentimentFlag)} />
                  )}
                </TwoRowsCell>
              </MobileRowValue>
            </MobileRow>
          </CustomMobileTableRowWrap>
        );
      })}
    </MobileTableWrapper>
  );
};

export default PublishedUpdatesMobileTable;

const CustomMobileTableRowWrap = styled(MobileTableRowWrap)`
  gap: 20px;
`;

const TwoRowsCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 0;
`;

const AssetClass = styled.p`
  font-size: 18px;
  font-weight: 400;
  line-height: 22.4px;
  padding: 0 4px;
  color: ${themeVar('font.weak')};
`;

const StrategyName = styled.p`
  font-size: 18px;
  font-weight: 600;
  line-height: 22.4px;
  padding: 0 4px;
  color: ${themeVar('font.action')};
`;

const CustomTextCell = styled.div`
  font-size: 18px;
  font-weight: 600;
  line-height: 22.4px;
  padding: 0 4px;
  color: ${themeVar('font.base')};
`;
